import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Filter,
    ArrowLeft,
    List,
    FileUp,
    Zap,
} from 'lucide-react';
import { contractNotesApi, contractFilesApi, orderAllocationApi, supportingDataApi } from '../services/api';
import { ContractFileUploadResponse, ContractNote, Scheme } from '../types/client';
import { SCHEME_REGION_GLOBAL } from '@/config/constants';
import { todayDateParam } from '@/utils/formatters';
import styles from './ContractNotes.module.css';
import {
    PageFrame, PageHeader, Surface, Button, Pill, Placeholder, Pager, Modal, FileDropzone, FileChip,
} from '@/components/ui';
import ContractNotesTable from '@/components/contract_notes/ContractNotesTable';
import shared from '@/components/utils/shared.module.css';

const ContractNoteProcess: React.FC = () => {
    const navigate = useNavigate();
    const [contractNoteDate, setContractNoteDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState<ContractFileUploadResponse | null>(null);

    const [groupedFiles, setGroupedFiles] = useState<{
        contract_note_date: string;
        file_count: number;
        file_names: string[];
    }[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(true);
    const [totalDates, setTotalDates] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(25);
    const [searchDate, setSearchDate] = useState('');

    const [selectedFileNotes, setSelectedFileNotes] = useState<{
        fileName: string;
        notes: ContractNote[];
    } | null>(null);

    // --- Global (IBKR) contract notes: no file to upload, built from the live session ---
    const [globalSchemes, setGlobalSchemes] = useState<Scheme[]>([]);
    const [selectedGlobalScheme, setSelectedGlobalScheme] = useState('');
    const [fetchingGlobal, setFetchingGlobal] = useState(false);
    const [globalErrors, setGlobalErrors] = useState<string[]>([]);

    const fetchContractFiles = useCallback(async () => {
        setIsLoadingFiles(true);
        try {
            const params: any = { page: currentPage, page_size: pageSize };
            if (searchDate) params.contract_note_date = searchDate;
            const response = await contractFilesApi.getContractFilesGrouped(params);
            setGroupedFiles(response.results);
            setTotalDates(response.count);
        } catch (error) {
            console.error('Error fetching contract files:', error);
            toast.error('Failed to load contract files');
        } finally {
            setIsLoadingFiles(false);
        }
    }, [currentPage, pageSize, searchDate]);

    useEffect(() => {
        fetchContractFiles();
    }, [fetchContractFiles]);

    useEffect(() => {
        supportingDataApi.getSchemes()
            .then(schemes => {
                const global = schemes.filter(s => s.region === SCHEME_REGION_GLOBAL && s.is_active);
                setGlobalSchemes(global);
                setSelectedGlobalScheme(prev => prev || global[0]?.code || '');
            })
            .catch(() => setGlobalSchemes([]));
    }, []);

    const handleFetchGlobalNotes = async () => {
        if (!selectedGlobalScheme) return;
        setFetchingGlobal(true);
        setGlobalErrors([]);
        try {
            const { result } = await orderAllocationApi.fetchGlobalContractNotes(selectedGlobalScheme);
            setGlobalErrors(result.errors);
            // Errors outrank a zero count: fills that all failed to build also leave
            // contract_notes at 0, and reporting that as an empty session tells the desk
            // there is nothing to look at.
            if (result.errors.length) {
                toast.warning(`${selectedGlobalScheme}: ${result.contract_notes} contract note(s) built, some fills skipped`);
            } else if (result.contract_notes === 0) {
                toast.info(`${selectedGlobalScheme}: no fills in the current IBKR session`);
                return;
            } else {
                toast.success(`${selectedGlobalScheme}: ${result.contract_notes} contract note(s) built`);
            }
            // Straight to the allocation cards — global notes have no file row to click through.
            navigate(`/contract-files-manage?date=${todayDateParam()}`);
        } catch (err: any) {
            const data = err?.response?.data;
            const message = data?.message || data?.error || 'Failed to fetch global contract notes';
            toast.error(message);
            setGlobalErrors([message]);
        } finally {
            setFetchingGlobal(false);
        }
    };

    const handleFileChange = (files: File[]) => {
        setSelectedFiles(files);
    };

    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedFiles.length === 0) {
            toast.warning('Please select at least one file');
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        selectedFiles.forEach(file => formData.append('files', file));
        formData.append('type', '1');
        formData.append('contract_note_date', contractNoteDate);

        try {
            const response = await contractNotesApi.uploadContractNotes(formData);
            setUploadResults(response);
            if (response.failed_uploads > 0) {
                toast.warning(`${response.successful_uploads} uploaded, ${response.failed_uploads} failed`);
            } else {
                toast.success(`Successfully uploaded ${response.successful_uploads} files`);
            }
            setSelectedFiles([]);
            fetchContractFiles();
        } catch (error: any) {
            console.error('Upload error:', error);
            if (error.response?.data && error.response.data.results) {
                setUploadResults(error.response.data);
                toast.error(`${error.response.data.successful_uploads} uploaded, ${error.response.data.failed_uploads} failed`);
            } else {
                const data = error.response?.data;
                const contractNoteDateErr = data?.contract_note_date;
                const errorMsg = contractNoteDateErr
                    ? (Array.isArray(contractNoteDateErr) ? contractNoteDateErr[0] : contractNoteDateErr)
                    : (data?.message || data?.detail || 'Failed to upload contract notes');
                setUploadResults({
                    total_files: selectedFiles.length,
                    successful_uploads: 0,
                    failed_uploads: selectedFiles.length,
                    results: selectedFiles.map(f => ({
                        file: f.name,
                        status: false,
                        message: errorMsg,
                        errors: [errorMsg],
                    })),
                });
                toast.error(errorMsg);
            }
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Contract Note Processing"
                    subtitle="Upload broker files and review what was parsed from them."
                    actions={
                        <Button onClick={() => navigate('/contract-notes')} icon={<ArrowLeft size={15} />}>
                            Back to List
                        </Button>
                    }
                />
            }
        >


            {/* ── Upload Section ── */}
            <Surface
                title={<span className={styles['title-with-icon']}><FileUp size={18} className={styles['section-icon']} />Upload Contract Notes</span>}
                padded
            >
                <form onSubmit={handleUpload} className={styles['upload-form']}>
                    <div className={styles['upload-form-group']}>
                        <label>Contract Note Date</label>
                        <input
                            type="date"
                            value={contractNoteDate}
                            onChange={(e) => setContractNoteDate(e.target.value)}
                            className={styles.input}
                            style={{ minWidth: '160px' }}
                        />
                    </div>

                    <FileDropzone
                        className={styles['upload-dropzone']}
                        title="Select Files (XLS, XLSX, CSV)"
                        accept={['.xls', '.xlsx', '.csv']}
                        multiple
                        reportEmpty
                        disableDrop
                        onFiles={handleFileChange}
                        inputId="file-input"
                        mainText={selectedFiles.length > 0
                            ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected`
                            : 'Click to browse files'}
                    />

                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isUploading || selectedFiles.length === 0}
                    >
                        {isUploading ? 'Uploading…' : 'Upload & Process'}
                    </Button>
                </form>

                {selectedFiles.length > 0 && (
                    <div className={styles['file-tags']}>
                        <div className={styles['file-tags-label']}>
                            Selected Files ({selectedFiles.length})
                        </div>
                        <div className={styles.tags}>
                            {selectedFiles.map((file, index) => (
                                <FileChip key={index} name={file.name} onRemove={() => removeFile(index)} />
                            ))}
                        </div>
                    </div>
                )}

                {uploadResults && (
                    <div className={styles['upload-results']}>
                        <div className={styles['result-pills']}>
                            <Pill tone="positive">Successful: {uploadResults.successful_uploads}</Pill>
                            <Pill tone="critical">Failed: {uploadResults.failed_uploads}</Pill>
                        </div>
                        {uploadResults.failed_uploads > 0 && (
                            <div className={styles['failed-list']}>
                                <div className={styles['failed-list-title']}>Failed Files</div>
                                {uploadResults.results
                                    .filter(r => !r.status)
                                    .map((r, idx) => (
                                        <div key={idx} className={styles['failed-item']}>
                                            <div className={styles['failed-item-name']}>{(r as any).file_name || (r as any).file || 'Unknown File'}</div>
                                            <div className={styles['failed-item-reason']}>
                                                {r.errors && r.errors.length > 0 ? (
                                                    <ul className={styles['failed-item-errors']}>
                                                        {r.errors.map((err, i) => (
                                                            <li key={i}>{err}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <span>{r.message}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                )}
            </Surface>

            {/* ── Global (IBKR) Section ── */}
            {globalSchemes.length > 0 && (
                <Surface
                    title={<span className={styles['title-with-icon']}><Zap size={18} className={styles['section-icon']} />Global (IBKR) Contract Notes</span>}
                    padded
                >
                    <div className={styles['upload-form']}>
                        <div className={styles['upload-form-group']}>
                            <label>Scheme</label>
                            <select
                                value={selectedGlobalScheme}
                                onChange={(e) => setSelectedGlobalScheme(e.target.value)}
                                disabled={fetchingGlobal}
                                className={styles.input}
                                style={{ minWidth: '160px' }}
                            >
                                {globalSchemes.map(scheme => (
                                    <option key={scheme.id} value={scheme.code}>{scheme.code}</option>
                                ))}
                            </select>
                        </div>

                        <div className={`${styles['upload-form-group']} ${styles['flex-grow']}`}>
                            <label>Today&apos;s IBKR session</label>
                            <span style={{ fontSize: '13px', color: 'var(--text-dim)' }}>
                                Global schemes have no file to upload — their contract notes are
                                built from the current IBKR session. Safe to run more than once:
                                each run restates the whole day.
                            </span>
                        </div>

                        <Button
                            onClick={handleFetchGlobalNotes}
                            disabled={fetchingGlobal || !selectedGlobalScheme}
                        >
                            {fetchingGlobal ? 'Fetching…' : 'Fetch Contract Notes'}
                        </Button>
                    </div>

                    {globalErrors.length > 0 && (
                        <div className={styles['failed-list']}>
                            <div className={styles['failed-list-title']}>Fills not processed</div>
                            <ul className={styles['failed-item-errors']}>
                                {globalErrors.map((err, i) => <li key={i}>{err}</li>)}
                            </ul>
                        </div>
                    )}
                </Surface>
            )}

            {/* ── Processed Files Section ── */}
            <Surface
                title={<span className={styles['title-with-icon']}><List size={18} className={styles['section-icon']} />Processed Contract Notes</span>}
                footer={!isLoadingFiles && totalDates > pageSize ? (
                    <Pager
                        page={currentPage}
                        totalPages={Math.ceil(totalDates / pageSize)}
                        onPageChange={setCurrentPage}
                    />
                ) : undefined}
            >
                <div className={styles['list-filters']}>
                    <div className={styles['filter-group']} style={{ maxWidth: '260px' }}>
                        <label>Processed Date</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input
                                type="date"
                                className={styles.input}
                                value={searchDate}
                                onChange={(e) => { setSearchDate(e.target.value); setCurrentPage(1); }}
                            />
                            {searchDate && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSearchDate(''); setCurrentPage(1); }}
                                    title="Clear date"
                                    aria-label="Clear date"
                                >
                                    ×
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    {isLoadingFiles ? (
                        <Placeholder kind="loading" message="Loading groups…" />
                    ) : (
                        <table className={shared.table}>
                            <thead>
                                <tr>
                                    <th>Processing Date</th>
                                    <th>File Names</th>
                                    <th className={shared['text-right']}>Files Count</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedFiles.length > 0 ? (
                                    groupedFiles.map((row) => (
                                        <tr key={row.contract_note_date}>
                                            <td style={{ fontWeight: 600 }}>
                                                {(() => { const [dd, mm, yyyy] = row.contract_note_date.split('-'); return new Date(`${yyyy}-${mm}-${dd}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); })()}
                                            </td>
                                            <td style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-dim)' }}>
                                                {row.file_names.join(', ')}
                                            </td>
                                            <td className={shared['text-right']}>
                                                <Pill solid plain>{row.file_count} files</Pill>
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    onClick={() => navigate(`/contract-files-manage?date=${row.contract_note_date}`)}
                                                    icon={<Filter size={13} />}
                                                >
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4}>
                                            <Placeholder message="No processed groups found" />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </Surface>

            {/* Notes Modal */}
            <Modal
                open={!!selectedFileNotes}
                title={`Notes: ${selectedFileNotes?.fileName ?? ''}`}
                onClose={() => setSelectedFileNotes(null)}
                maxWidth={1100}
                titleId="contract-process-notes-title"
                closeOnEscape={false}
            >
                {selectedFileNotes && <ContractNotesTable notes={selectedFileNotes.notes} />}
            </Modal>
        </PageFrame>
    );
};

export default ContractNoteProcess;